import { useMutation, useQueryClient } from '@tanstack/react-query';
import * as Effect from 'effect/Effect';
import { pipe } from 'effect/Function';
import { GroupService } from '@/effects/groups/group.service';
import { MainLayer } from '@/effects/layers';
import { runEffect } from '@/effects/runtime';
import { QUERY_KEYS } from '@/lib/constants';
import { ApiClient } from '@/api/client';
import { toast } from 'sonner';
import type { CreateGroupParams, UpdateGroupParams } from '@/effects/groups/group.service';
import { logger } from '@/lib/logger';

export function useCreateGroup() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['group', 'create'],
    mutationFn: async (params: CreateGroupParams) => {
      const program = pipe(
        GroupService,
        Effect.flatMap((service) => service.create(params)),
        Effect.provide(MainLayer)
      );

      return runEffect(program);
    },
    onSuccess: () => {
      // Invalidate user groups to refresh the list
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.USER_GROUPS],
      });
    },
  });
}

export function useUpdateGroup() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['group', 'update'],
    mutationFn: async (params: UpdateGroupParams) => {
      const program = pipe(
        GroupService,
        Effect.flatMap((service) => service.update(params)),
        Effect.provide(MainLayer)
      );

      return runEffect(program);
    },
    onSuccess: (_, params) => {
      // Invalidate user groups to refresh the list
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.USER_GROUPS],
      });
      // Also invalidate the specific group
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.GROUPS, params.groupId],
      });
    },
  });
}

export function useInviteToGroup() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['group', 'invite'],
    mutationFn: async (groupId: string) => {
      const program = pipe(
        GroupService,
        Effect.flatMap((service) => service.createInvitation(groupId)),
        Effect.provide(MainLayer)
      );

      return runEffect(program);
    },
    onSuccess: (data, _groupId) => {
      // Invalidate group data to show new invitation
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.USER_GROUPS],
      });

      // Log for debugging
      logger.debug('[useInviteToGroup] Invitation created:', { data });
    },
    onError: (error) => {
      logger.error('[useInviteToGroup] Failed to create invitation:', { error });
    },
  });
}

export function useSetPinnedCategories() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['group', 'setPinnedCategories'],
    mutationFn: async (params: { groupId: string; categoryIds: string[] }) => {
      const program = pipe(
        GroupService,
        Effect.flatMap((service) =>
          service.setPinnedCategories(params)
        ),
        Effect.provide(MainLayer)
      );

      return runEffect(program);
    },
    onSuccess: (_, params) => {
      // Invalidate user groups to refresh pinned categories
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.USER_GROUPS],
      });

      // Also invalidate specific group data
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.GROUPS, params.groupId],
      });
    },
  });
}

export function useJoinGroupWithCode() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['group', 'join-with-code'],
    mutationFn: async (inviteCode: string) => {
      // Create the effect program to join group with invite code
      const program = pipe(
        ApiClient,
        Effect.flatMap((api) =>
          api.post('/groups/join', { inviteCode })
        ),
        Effect.provide(MainLayer)
      );

      return runEffect(program);
    },
    onSuccess: (_data) => {
      // Invalidate user groups to show the newly joined group
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.USER_GROUPS],
      });

      toast.success(`Successfully joined group!`);
    },
    onError: (error) => {
      logger.error('Failed to join group:', { error });
      toast.error(error instanceof Error ? error.message : 'Invalid or expired invitation code');
    },
  });
}